/**
 * What "still open" counts, once: the report's section and a run's end card both
 * read it, so the line that says "4 still open" opens a section listing those 4.
 *
 * It was the section's own three filters until the card needed the same count:
 * the card first counted the run's "open ends left" — every claim with a verb the
 * policy could still spend, parents included — and a reader pressing "6" landed
 * on "4 claims are still open". Two rules under one name is D155's failure.
 *
 * Three ways a claim is open, in the order the section lists them:
 *
 *  - `gap` — a stated unknown, closed in reasoning or not, that no recorded
 *    result has answered. A gap the model closed is "open: reasoning can't
 *    settle it", which waits on the reader; it is the opposite of finished.
 *  - `unsettled` — a result was recorded on it and settled nothing.
 *  - `leaf` — an end nobody has taken further: not closed, no result, nothing
 *    derived from it. Kinds with a section of their own — a reported result, an
 *    assumption, a proposed test, an objection, a conjecture — are read there,
 *    and a gap is the first class already.
 *
 * Read off stored fields and one structural fact the caller supplies (whether
 * anything was derived from it), so it holds no index of its own.
 */

import type { NodeKind, ReasonNode } from './types'
import { endingOf } from './endings'

export type StillOpen = 'gap' | 'unsettled' | 'leaf'

/** The order the section lists them in: stated unknowns first. */
export const STILL_OPEN_ORDER: readonly StillOpen[] = ['gap', 'unsettled', 'leaf']

/** Kinds an open end is read under elsewhere in the report. A conjecture has had
 *  its own section since D288, with its kill test where a next step would be. */
const READ_ELSEWHERE: ReadonlySet<NodeKind> = new Set<NodeKind>([
  'gap',
  'evidence',
  'assumption',
  'experiment',
  'contradiction',
  'conjecture',
])

/** How a claim is still open, or null when it is not. `derived`: whether any
 *  claim has been reasoned from it. The seed is never "still open" here. */
export function stillOpenAs(n: ReasonNode, derived: boolean): StillOpen | null {
  if (n.parentId === null) return null
  if (n.kind === 'gap') return endingOf(n) === 'answered' ? null : 'gap'
  if (n.outcome?.bearing === 'inconclusive') return 'unsettled'
  if (!n.terminal && !n.outcome && !derived && !READ_ELSEWHERE.has(n.kind)) return 'leaf'
  return null
}
